import React, { useRef, useState, useContext, useCallback, useEffect } from 'react';
import { Button, sparkToast, TooltipTrigger, PopoverGeneral, SparkIcon, Text, ReadOnlyGuard } from '@spark';
import UploadDataIcon from '@/shared/assets/icons/UploadDataIcon.png';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { checkDatasetExists } from '@/features/datasets/api/datasetApi';
import DatasetUploadStepsModal from '@/features/datasets/components/DatasetUploadStepsModal';
import { aiLabelRowsToMapping } from '@/features/tasks/utils/aiLabels';
import {
    NO_LABELS_MODE_UNLABELED,
    shouldSubmitAiLabelMappings,
    isGenerativeAiEmptyColumnTask,
} from '@/features/datasets/utils/hasLabelsHandling';
import DatasetExistsModal from '@/features/datasets/components/DatasetExistsModal';
import RenameUploadModal from '@/features/datasets/components/RenameUploadModal';
import { useAuth } from '@/features/auth/AuthContext';
import ExternalModelResultsUpload from '@/features/datasets/components/DataCollection/ExternalModelResultsUpload';
import { GlobalContext } from '@/app/providers/globalState';
import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';
import {
    acceptAttributeForTaskType,
    allowedFormatsForTaskType,
    readDatasetFileContent,
    templateLinksForTaskType,
    uploadDatasetFile,
    MAX_FILE_CHUNK_SIZE,
} from '@/features/datasets/utils/datasetUploadFlow';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';

/** Same File instance must not start the mapping flow twice (React Strict Mode remounts). */
const consumedDataOverviewDroppedFiles = new WeakSet();

const UploadDataTab = ({
    currentUsecase,
    handleUploadDatasetSuccess,
    handleUploadBenchmarkSuccess,
    handleUploadExternalSuccess,
    selectedTaskType,
    /** `'benchmark'` hides the train-set upload and promotes benchmark as primary. */
    uploadTarget,
    /** OS files dropped on Data Overview Datasets/Benchmarks — starts mapping flow. */
    initialDroppedFiles = null,
    onInitialDroppedFilesConsumed,
    onUploadSuccess,
    setIsDataAnalysisLoading,
    refreshLibraryOptions,
    onDataDetailsVisibilityChange,
    onUploadFlowDone,
}) => {
    const { role } = useAuth();
    const { setProgressStatus, updateUsecase, registerUploadAbort, unregisterUploadAbort, registerUploadGroup, unregisterUploadGroup, clearCancelledUploadSuppression, currentSubGroup } = useContext(GlobalContext);
    const fileInputRef = useRef(null);
    const benchmarkFileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isBenchmarkDragOver, setIsBenchmarkDragOver] = useState(false);
    const [showOriginModal, setShowOriginModal] = useState(false);
    const [showDatasetExistsModal, setShowDatasetExistsModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [pendingFileType, setPendingFileType] = useState(null);
    const [selectedOrigin, setSelectedOrigin] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [renamedFileName, setRenamedFileName] = useState(null);
    const [delimiter, setDelimiter] = useState(null);
    const [renameReason, setRenameReason] = useState(null); // 'exists' | 'too-long' | null
    const [helpOpen, setHelpOpen] = useState(false);
    const [allLabelOptions, setAllLabelOptions] = useState(null);

    useEffect(() => {
        if (!currentUsecase?._id) return;
        data_from_server_post('get-labels', currentUsecase._id, (response) => {
            setAllLabelOptions(response || {});
        });
    }, [currentUsecase?._id, showOriginModal]);

    useEffect(() => {
        // Hide the parent "Upload Data" modal whenever any follow-up dialog is
        // shown (steps modal, dataset-exists confirmation, or rename modal),
        // so they don't render stacked on top of the upload modal.
        const anyFollowUpOpen = showOriginModal || showDatasetExistsModal || showRenameModal;
        onDataDetailsVisibilityChange?.(anyFollowUpOpen);
    }, [showOriginModal, showDatasetExistsModal, showRenameModal, onDataDetailsVisibilityChange]);

    const mergeUnique = useCallback((base = [], additions = []) => {
        return [...new Set([...(Array.isArray(base) ? base : []), ...(Array.isArray(additions) ? additions : [])])]
            .filter(Boolean);
    }, []);

    const applyMappingsToLabelOptions = useCallback((prevOptions, mappings, relMappings) => {
        console.log('mappings is:', mappings);
        console.log('relMappings is:', relMappings);
        console.log('prevOptions is:', prevOptions);
        const prev = prevOptions || {};
        // Treat keys explicitly mapped to null as "deleted" — drop them from both the
        // contribution of mapped keys and from the mapped values list.
        const mappedValues = Object.values(mappings || {}).filter((v) => v && v !== 'none');
        const mappedKeys = Object.entries(mappings || {})
            .filter(([k, v]) => Boolean(k) && v !== null)
            .map(([k]) => k);
        const relValues = Object.values(relMappings || {}).filter((v) => v && v !== 'none');
        const relKeys = Object.entries(relMappings || {})
            .filter(([k, v]) => Boolean(k) && v !== null)
            .map(([k]) => k);

        if (selectedTaskType === TaskType.classification) {
            return {
                ...prev,
                label_names: mergeUnique(prev?.label_names, [...mappedKeys, ...mappedValues]),
            };
        }
        if (selectedTaskType === TaskType.NER) {
            return {
                ...prev,
                entity_names: mergeUnique(prev?.entity_names, [...mappedKeys, ...mappedValues]),
            };
        }
        if (selectedTaskType === TaskType.NER_WITH_RELATIONS) {
            return {
                ...prev,
                entity_names: mergeUnique(prev?.entity_names, [...mappedKeys, ...mappedValues]),
                relation_names: mergeUnique(prev?.relation_names, [...relKeys, ...relValues]),
            };
        }
        return prev;
    }, [mergeUnique, selectedTaskType]);

    // Close the steps modal and then open the Help popup.
    // IMPORTANT: we intentionally do NOT call handleOriginModalCancel() here, so we don't clear the pending upload state.
    const openHelpFromStepsModal = useCallback(() => {
        setShowOriginModal(false);
        // Defer opening by a tick so the modal overlay unmounts first.
        setTimeout(() => setHelpOpen(true), 0);
    }, []);

    const uploadInstructions = (
        <>
            Click{' '}
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    setHelpOpen(true);
                }}
                style={{
                    background: 'none',
                    border: 'none',
                    color: '#1777ff',
                    cursor: 'pointer',
                    font: 'inherit',
                    padding: 0,
                    textDecoration: 'underline',
                }}
            >
                Need help?
            </button>{' '}
            for an example
        </>
    );

    const isAgentic = isAgenticAutonomyLevel(currentUsecase?.autonomy_level);
    const filesAllowed = acceptAttributeForTaskType(selectedTaskType, { isAgentic });

    const checkAndHandleFileUpload = async (file, fileType) => {
        const fileFormat = detectFileFormat(file);
        const allowed = allowedFormatsForTaskType(selectedTaskType, { isAgentic });

        if (!fileFormat || !allowed.has(fileFormat)) {
            const allowedText = Array.from(allowed).map(f => f.toUpperCase()).join(', ');
            sparkToast.error(`Invalid file type. Please upload a ${allowedText} file.`);
            return;
        }

        setPendingFile(file);
        setPendingFileType(fileType);
        setFileType(fileFormat);

        // Reset the selected origin for new file upload
        setSelectedOrigin(null);

        // Read file content asynchronously
        try {
            const shouldSampleContent = file.size > MAX_FILE_CHUNK_SIZE;
            const content = await readDatasetFileContent(file, { chunkOnly: shouldSampleContent });
            setFileContent(content);

            // Check if dataset already exists
            const datasetName = file.name.split('.')[0]; // Remove file extension
            const datasetExists = await checkDatasetExists(datasetName);

            if (datasetExists) {
                setRenameReason('exists');
                // Show dataset exists modal
                setShowDatasetExistsModal(true);
            } else if (datasetName.length > 40) {
                // If doesn't exist but name too long, show rename modal directly
                setRenameReason('too-long');
                setShowRenameModal(true);
            } else {
                // Proceed with normal upload flow
                setShowOriginModal(true);
            }
        } catch (error) {
            console.error('Error reading file:', error);
            sparkToast.error('Error reading file content');
        }
    };

    // Data Overview drag-and-drop: open this tab with dropped files and jump
    // straight into the same mapping / rename flow as a click upload. Legacy
    // single-file flow — only the first dropped file is mapped here.
    useEffect(() => {
        const files = Array.isArray(initialDroppedFiles)
            ? initialDroppedFiles.filter((f) => f instanceof File)
            : [];
        const initialDroppedFile = files[0];
        if (!initialDroppedFile) return;
        if (consumedDataOverviewDroppedFiles.has(initialDroppedFile)) return;
        if (role === 'Viewer') {
            onInitialDroppedFilesConsumed?.();
            return;
        }
        consumedDataOverviewDroppedFiles.add(initialDroppedFile);
        const fileType = uploadTarget === 'benchmark' ? 'benchmark' : 'dataset';
        onInitialDroppedFilesConsumed?.();
        void checkAndHandleFileUpload(initialDroppedFile, fileType);
        // Intentionally only re-run when a new dropped-file batch arrives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDroppedFiles]);

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        await checkAndHandleFileUpload(file, 'dataset');
    };

    const handleBenchmarkFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        await checkAndHandleFileUpload(file, 'benchmark');
    };

    const uploadFile = async (
        file,
        type,
        datasetOrigin = null,
        labelMappings = {},
        exampleColumnName = "example",
        labelColumnName = "labels",
        relationsColumnName = "",
        relationsMappings = {},
        examplesAreWithPrompt = false,
        promptTemplate = "",
        dynamicFieldsInPromptTemplate = [],
        exampleDynamicField = null,
        originLabelsColumnName = "",
        subtaskMappings = null,
        rejectionLabelName = null,
        emptyMultilabelHandling = 'none_selected',
        generateResponsesWithAi = false
    ) => {
        await uploadDatasetFile({
            file,
            type,
            currentUsecase,
            selectedTaskType,
            renamedFileName,
            delimiter,
            datasetOrigin,
            labelMappings,
            exampleColumnName,
            labelColumnName,
            relationsColumnName,
            relationsMappings,
            examplesAreWithPrompt,
            promptTemplate,
            dynamicFieldsInPromptTemplate,
            exampleDynamicField,
            originLabelsColumnName,
            subtaskMappings,
            rejectionLabelName,
            emptyMultilabelHandling,
            generateResponsesWithAi,
            context: {
                setProgressStatus,
                updateUsecase,
                registerUploadAbort,
                unregisterUploadAbort,
                registerUploadGroup,
                clearCancelledUploadSuppression,
                currentSubGroup,
            },
            callbacks: {
                onDatasetSuccess: handleUploadDatasetSuccess,
                onBenchmarkSuccess: handleUploadBenchmarkSuccess,
                onUploadSuccess,
                refreshLibraryOptions,
                setIsDataAnalysisLoading,
                onLabelMappingsApplied: (mappings, relMappings) =>
                    setAllLabelOptions((prev) =>
                        applyMappingsToLabelOptions(prev, mappings, relMappings)
                    ),
            },
        });

    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            await checkAndHandleFileUpload(file, 'dataset');
        }
    };

    const handleBenchmarkDragOver = (e) => {
        e.preventDefault();
        if (role === 'Viewer') return;
        setIsBenchmarkDragOver(true);
    };

    const handleBenchmarkDragLeave = (e) => {
        e.preventDefault();
        if (role === 'Viewer') return;
        setIsBenchmarkDragOver(false);
    };

    const handleBenchmarkDrop = async (e) => {
        e.preventDefault();
        if (role === 'Viewer') return;
        setIsBenchmarkDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            await checkAndHandleFileUpload(file, 'benchmark');
        }
    };

    const handleOriginModalConfirm = async (
        datasetOrigin,
        labelMappings = {},
        exampleColumnName = "example",
        labelColumnName = "labels",
        relationsColumnName = "",
        relationsMappings = {},
        examplesAreWithPrompt = false,
        promptTemplate = "",
        dynamicFieldsInPromptTemplate = [],
        exampleDynamicField = null,
        originLabelsColumnName = "",
        subtaskMappings = null,
        rejectionLabelName = null,
        emptyMultilabelHandling = 'none_selected',
        generateResponsesWithAi = false
    ) => {
        setShowOriginModal(false);
        // The user has finished interacting with the upload flow (Done/Next/Skip
        // all funnel through here); progress is tracked elsewhere, so let the
        // parent close its containing "Upload Data" modal entirely.
        onUploadFlowDone?.();
        if (pendingFile && pendingFileType) {
            setPendingFile(null);
            setPendingFileType(null);
            setRenamedFileName(null);
            await uploadFile(
                pendingFile,
                pendingFileType,
                datasetOrigin,
                labelMappings,
                exampleColumnName,
                labelColumnName,
                relationsColumnName,
                relationsMappings,
                examplesAreWithPrompt,
                promptTemplate,
                dynamicFieldsInPromptTemplate,
                exampleDynamicField,
                originLabelsColumnName,
                subtaskMappings,
                rejectionLabelName,
                emptyMultilabelHandling,
                generateResponsesWithAi
            );
        }
    };

    const handleOriginModalCancel = () => {
        setShowOriginModal(false);
        setPendingFile(null);
        setPendingFileType(null);
        setSelectedOrigin(null);
        setRenamedFileName(null);
        setFileContent(null);
    };

    const handleSkip = () => {
        handleOriginModalConfirm('skip');
    };

    const handleNext = () => {
        // For now, just proceed with upload since we don't have label review functionality in this component
        handleOriginModalConfirm(selectedOrigin);
    };

    const handleDone = (
        labelMappings = {},
        exampleColumnName = "example",
        labelColumnName = "labels",
        relationsColumnName = "",
        relationsMappings = {},
        examplesAreWithPrompt = false,
        promptTemplate = "",
        dynamicFieldsInPromptTemplate = [],
        exampleDynamicField = null,
        originLabelsColumnName = "",
        subtaskMappings = null,
        rejectionLabelName = null,
        emptyMultilabelHandling = 'none_selected',
        generateResponsesWithAi = false
    ) => {
        handleOriginModalConfirm(
            selectedOrigin,
            labelMappings,
            exampleColumnName,
            labelColumnName,
            relationsColumnName,
            relationsMappings,
            examplesAreWithPrompt,
            promptTemplate,
            dynamicFieldsInPromptTemplate,
            exampleDynamicField,
            originLabelsColumnName,
            subtaskMappings,
            rejectionLabelName,
            emptyMultilabelHandling,
            generateResponsesWithAi
        );
    };

    const handleDatasetExistsModalCancel = () => {
        setShowDatasetExistsModal(false);
        setPendingFile(null);
        setPendingFileType(null);
        setFileContent(null);
        setRenameReason(null);
    };

    const handleUseExistingDataset = () => {
        setShowDatasetExistsModal(false);
        setShowOriginModal(true);
    };

    const handleRenameUpload = () => {
        setShowDatasetExistsModal(false);
        setShowRenameModal(true);
    };

    const handleRenameModalCancel = () => {
        setShowRenameModal(false);
        setPendingFile(null);
        setPendingFileType(null);
        setFileContent(null);
        setRenamedFileName(null);
        setRenameReason(null);
    };

    const handleRenameModalBack = () => {
        setShowRenameModal(false);
        if (renameReason === 'exists') {
            setShowDatasetExistsModal(true);
        } else {
            // If rename was due to length, go back to initial state
            setPendingFile(null);
            setPendingFileType(null);
            setFileContent(null);
            setRenameReason(null);
        }
    };

    const handleRenameModalDone = (newFileName) => {
        setRenamedFileName(newFileName);
        setShowRenameModal(false);
        setShowOriginModal(true);
        setRenameReason(null);
    };

    const hasBenchmarks = currentUsecase?.benchmarks?.length > 0;
    const isBenchmarkOnly = uploadTarget === 'benchmark';
    const primaryUploadLabel = isBenchmarkOnly ? 'Upload Benchmark' : 'Upload Train Set';
    const primaryViewerTooltip = isBenchmarkOnly
        ? 'Viewers cannot upload benchmarks'
        : 'Viewers cannot upload train set';
    const primaryDragOver = isBenchmarkOnly ? isBenchmarkDragOver : isDragOver;
    const primaryFileInputRef = isBenchmarkOnly ? benchmarkFileInputRef : fileInputRef;
    const handlePrimaryDragOver = isBenchmarkOnly ? handleBenchmarkDragOver : handleDragOver;
    const handlePrimaryDragLeave = isBenchmarkOnly ? handleBenchmarkDragLeave : handleDragLeave;
    const handlePrimaryDrop = isBenchmarkOnly ? handleBenchmarkDrop : handleDrop;
    const handlePrimaryFileSelect = isBenchmarkOnly ? handleBenchmarkFileSelect : handleFileSelect;

    // Close the Help popover on outside click (mirrors the antd Popover's
    // trigger="click" + onOpenChange behavior).
    const helpPopoverRef = useRef(null);
    useEffect(() => {
        if (!helpOpen) return undefined;
        const handleOutside = (e) => {
            if (helpPopoverRef.current && !helpPopoverRef.current.contains(e.target)) {
                setHelpOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [helpOpen]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', position: 'relative' }}>
            {/* Help Popup - aligned with the parent modal title (offsets
                compensate for the modal body padding, see
                .task-upload-data-modal in task-drawer.css) */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'absolute',
                top: '-51px',
                right: '36px',
                zIndex: 1,
            }}>
                <div ref={helpPopoverRef} style={{ position: 'relative' }}>
                    <span
                        onClick={() => setHelpOpen((prev) => !prev)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            color: '#262626',
                            fontWeight: 400,
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                    >
                        Need help?{' '}
                        <span style={{ color: '#8c8c8c', display: 'inline-flex' }}>
                            <SparkIcon slug="circle-help" size="small" />
                        </span>
                    </span>
                    {helpOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1200 }}>
                            <PopoverGeneral tip={false} header={false} footer={false}>
                                <div style={{ width: 268 }}>
                                    <Text variant="heading-5-semibold" as="h5" style={{ marginTop: 0 }}>
                                        Example Datasets
                                    </Text>
                                    <Text variant="base-regular" style={{ display: 'block', marginBottom: 8, color: '#444', fontWeight: 400, fontSize: 14 }}>
                                        Download an example dataset to see how your file should be structured
                                    </Text>
                                    <div>
                                        {templateLinksForTaskType(selectedTaskType, {
                                            isMultilabel: currentUsecase?.is_multilabel === true,
                                            isAgentic,
                                        }).map((link, idx) => (
                                            <div key={idx}>
                                                <a href={link.href} download style={{ color: '#1677ff', fontSize: 14, display: 'block', marginBottom: 2 }}>
                                                    {link.text}
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </PopoverGeneral>
                        </div>
                    )}
                </div>
            </div>
            {/* Primary upload area — train set by default; benchmark when scoped */}
            <ReadOnlyGuard block>
                <div
                    className="fs-unmask"
                    style={{
                        marginTop: 0,
                        background: primaryDragOver ? '#f0f8ff' : '#fafafa',
                        border: `1px dashed ${primaryDragOver ? '#1777ff' : '#d9d9d9'}`,
                        borderRadius: '8px',
                        padding: '20px 20px',
                        textAlign: 'center',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                    }}
                    onDragOver={handlePrimaryDragOver}
                    onDragLeave={handlePrimaryDragLeave}
                    onDrop={handlePrimaryDrop}
                    onClick={() => primaryFileInputRef.current?.click()}
                >
                    <img
                        src={UploadDataIcon}
                        alt=""
                        aria-hidden="true"
                        style={{
                            width: 130,
                            height: 130,
                            objectFit: 'contain',
                            marginBottom: '-10px',
                            marginTop: '-25px',
                        }}
                    />
                    <div style={{
                        fontSize: '16px',
                        fontWeight: 400,
                        color: '#262626',
                        marginBottom: '2px'
                    }}>
                        Click or drag file to this area to upload
                    </div>
                    <div style={{
                        color: '#8c8c8c',
                        fontSize: '14px',
                        fontWeight: 300,
                        lineHeight: '1.5',
                        maxWidth: '400px',
                        margin: '0 auto',
                        marginBottom: '16px'
                    }}>
                        {uploadInstructions}
                    </div>

                    {role === 'Viewer' ? (
                        <TooltipTrigger text={primaryViewerTooltip} placement="top">
                            <Button
                                variant="primary"
                                icon={<SparkIcon slug="upload" size="small" />}
                                size="large"
                                style={{
                                    height: '32px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: '#D1D5DB',
                                    borderColor: '#D1D5DB',
                                    padding: '0 30px',
                                }}
                                disabled
                                onClick={(e) => {
                                    e.stopPropagation();
                                    primaryFileInputRef.current?.click();
                                }}
                            >
                                {primaryUploadLabel}
                            </Button>
                        </TooltipTrigger>
                    ) : (
                        <Button
                            variant="primary"
                            icon={<SparkIcon slug="upload" size="small" />}
                            size="large"
                            style={{
                                height: '32px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                backgroundColor: '#1777ff',
                                borderColor: '#1777ff',
                                padding: '0 30px',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                primaryFileInputRef.current?.click();
                            }}
                        >
                            {primaryUploadLabel}
                        </Button>
                    )}

                    <input
                        ref={primaryFileInputRef}
                        type="file"
                        accept={filesAllowed}
                        onChange={handlePrimaryFileSelect}
                        style={{ display: 'none' }}
                    />
                </div>
            </ReadOnlyGuard>

            {/* Secondary Upload Benchmark — hidden when the modal is benchmark-scoped */}
            {!isBenchmarkOnly && (role === 'Viewer' ? (
                <TooltipTrigger text="Viewers cannot upload benchmarks" placement="top">
                    <div
                        className="fs-unmask"
                        style={{
                            background: isBenchmarkDragOver ? '#f0f8ff' : '#fafafa',
                            border: `1px dashed ${isBenchmarkDragOver ? '#1777ff' : '#d9d9d9'}`,
                            borderRadius: '8px',
                            padding: '10px 20px',
                            textAlign: 'center',
                            position: 'relative',
                            transition: 'all 0.3s ease',
                            cursor: 'not-allowed',
                            color: '#D1D5DB',
                            marginTop: '-15px',
                        }}
                        onDragOver={handleBenchmarkDragOver}
                        onDragLeave={handleBenchmarkDragLeave}
                        onDrop={handleBenchmarkDrop}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    >
                        <span style={{ marginRight: '8px', display: 'inline-flex', verticalAlign: 'middle' }}>
                            <SparkIcon slug="download" size="small" />
                        </span>
                        Upload Benchmark

                        <input
                            ref={benchmarkFileInputRef}
                            type="file"
                            accept={filesAllowed}
                            onChange={handleBenchmarkFileSelect}
                            style={{ display: 'none' }}
                        />
                    </div>
                </TooltipTrigger>
            ) : (
                <ReadOnlyGuard block>
                    <div
                        className="fs-unmask"
                        style={{
                            background: isBenchmarkDragOver ? '#f0f8ff' : '#fafafa',
                            border: `1px dashed ${isBenchmarkDragOver ? '#1777ff' : '#d9d9d9'}`,
                            borderRadius: '8px',
                            padding: '10px 20px',
                            textAlign: 'center',
                            position: 'relative',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            color: '#1777ff',
                            marginTop: '-15px',
                        }}
                        onDragOver={handleBenchmarkDragOver}
                        onDragLeave={handleBenchmarkDragLeave}
                        onDrop={handleBenchmarkDrop}
                        onClick={() => benchmarkFileInputRef.current?.click()}
                    >
                        <span style={{ marginRight: '8px', display: 'inline-flex', verticalAlign: 'middle' }}>
                            <SparkIcon slug="download" size="small" />
                        </span>
                        Upload Benchmark

                        <input
                            ref={benchmarkFileInputRef}
                            type="file"
                            accept={filesAllowed}
                            onChange={handleBenchmarkFileSelect}
                            style={{ display: 'none' }}
                        />
                    </div>
                </ReadOnlyGuard>
            ))}

            {/* External Model Results Upload - Only show if usecase has benchmarks */}
            {hasBenchmarks && (
                <ExternalModelResultsUpload
                    currentUsecase={currentUsecase}
                    allLabelOptions={allLabelOptions}
                    onUploadSuccess={() => {
                        if (handleUploadExternalSuccess) {
                            handleUploadExternalSuccess();
                        }
                        if (onUploadSuccess) {
                            onUploadSuccess();
                        }
                    }}
                />
            )}

            {/* Dataset Exists Modal */}
            <DatasetExistsModal
                visible={showDatasetExistsModal}
                onCancel={handleDatasetExistsModalCancel}
                onUseExisting={handleUseExistingDataset}
                onRenameUpload={handleRenameUpload}
                fileName={pendingFile?.name?.split('.')[0]}
                loading={false}
            />

            {/* Rename Upload Modal */}
            <RenameUploadModal
                visible={showRenameModal}
                onCancel={handleRenameModalCancel}
                onBack={handleRenameModalBack}
                onDone={handleRenameModalDone}
                originalFileName={pendingFile?.name?.split('.')[0]}
                fileExtension={pendingFile?.name?.split('.').pop()}
                loading={false}
                infoMessage={renameReason === 'too-long' ? 'File name must be less than 40 characters. Please rename your file.' : undefined}
            />

            {/* File Origin Modal */}
            <DatasetUploadStepsModal
                visible={showOriginModal}
                onCancel={handleOriginModalCancel}
                fileName={renamedFileName ? `${renamedFileName}.${pendingFile?.name?.split('.').pop()}` : pendingFile?.name}
                selectedOrigin={selectedOrigin}
                onOriginChange={setSelectedOrigin}
                onOpenHelp={openHelpFromStepsModal}
                onSkip={handleSkip}
                onNext={handleNext}
                onDone={handleDone}
                uploading={false}
                clientId={currentUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75"}
                llmUsecaseId={currentUsecase?._id}
                allLabelOptions={allLabelOptions}
                uploadedFileName={renamedFileName || pendingFile?.name?.split('.')[0]}
                uploadedFileContent={fileContent}
                uploadedFileType={fileType}
                uploadedFile={pendingFile}
                taskType={selectedTaskType}
                isMultilabel={currentUsecase?.is_multilabel}
                existingRejectionLabelName={currentUsecase?.rejection_label_name || null}
                confidenceRejectionEnabled={currentUsecase?.confidence_rejection_enabled === true}
                delimiter={delimiter}
                setDelimiter={setDelimiter}
                subGroup={currentSubGroup}
                onDoneObject={(payload) => {
                    // Multi-objective mode: `payload.outputs` is keyed by every
                    // objective's _id under the active sub_group. The active
                    // tab's slice still drives the legacy single-task form
                    // fields (label_column_name, label_mappings, ...) so the
                    // backend route stays compatible. The full per-objective
                    // shape is forwarded as `subtask_mappings` JSON, which
                    // activates the multi-objective branch in
                    // `_parse_subtask_mappings_form` (>=2 entries).
                    const outputs = payload?.outputs || {};
                    const out = outputs[currentUsecase?._id] || {};
                    const exampleColumnName = payload?.examples?.exampleColumnName;

                    const siblings = currentSubGroup?.usecases || [];
                    const subtaskMappings = siblings
                        .map((uc) => {
                            const slice = outputs[uc._id];
                            if (!slice) return null;
                            const taskKey = uc.usecase_name || uc._id;
                            const optedOut = slice.hasLabels === false;
                            const labelColumn = optedOut
                                ? ''
                                : slice.labelColumn || slice.summaryColumn || '';
                            // Opted out + generate-with-AI → ship AI catalog as
                            // label_mappings (class/NER). Unlabeled → empty mappings.
                            const submitAi = shouldSubmitAiLabelMappings(slice);
                            const isClassOrNer =
                                uc.task_type === 'ClassificationResponse' ||
                                uc.task_type === TaskType.NER ||
                                uc.task_type === TaskType.NER_WITH_RELATIONS;
                            const aiLabelMappings =
                                submitAi && isClassOrNer
                                    ? aiLabelRowsToMapping(slice.aiLabels || [])
                                    : null;
                            const labelMappings = aiLabelMappings
                                ? aiLabelMappings
                                : optedOut
                                  ? {}
                                  : slice.labelMappings || {};
                            // Classification only — snapshot original labels column
                            // when generating with AI (not for plain unlabeled).
                            const originLabelsColumnName =
                                submitAi && uc.task_type === 'ClassificationResponse'
                                    ? slice.detectedLabelColumn || ''
                                    : '';
                            const rejectionLabelName =
                                uc.task_type === 'ClassificationResponse' &&
                                uc.is_multilabel !== true
                                    ? slice.rejectionLabelName || null
                                    : null;
                            const emptyMultilabelHandling =
                                slice.emptyMultilabelHandling ||
                                (uc.task_type === 'ClassificationResponse' &&
                                uc.is_multilabel === true
                                    ? 'none_selected'
                                    : 'unlabeled');
                            const isMultilabelClassification =
                                uc.task_type === 'ClassificationResponse' &&
                                uc.is_multilabel === true;
                            return {
                                task_key: taskKey,
                                llm_usecase_id: uc._id,
                                task_type: uc.task_type,
                                label_column_name: labelColumn,
                                label_mappings: labelMappings,
                                relations_column_name: optedOut
                                    ? ''
                                    : slice.relationsColumn || '',
                                relations_mappings: slice.relationsMappings || {},
                                classification_labels_delimiter: isMultilabelClassification
                                    ? slice.delimiter || 'list'
                                    : slice.delimiter || null,
                                empty_multilabel_handling: emptyMultilabelHandling,
                                origin_labels_column_name: originLabelsColumnName,
                                rejection_label_name: rejectionLabelName,
                                import_without_labels:
                                    optedOut &&
                                    slice.noLabelsMode === NO_LABELS_MODE_UNLABELED,
                                generate_responses_with_ai:
                                    optedOut &&
                                    slice.noLabelsMode !== NO_LABELS_MODE_UNLABELED &&
                                    isGenerativeAiEmptyColumnTask(uc.task_type),
                            };
                        })
                        // Keep entries that have a column, AI catalog, origin
                        // snapshot, opted-out empty column, or generative empty col.
                        .filter(
                            (entry) =>
                                entry &&
                                (entry.label_column_name ||
                                    Object.keys(entry.label_mappings || {}).length > 0 ||
                                    entry.origin_labels_column_name ||
                                    entry.import_without_labels ||
                                    entry.generate_responses_with_ai ||
                                    entry.task_type === TaskType.summarization ||
                                    entry.task_type === TaskType.contentGeneration ||
                                    entry.task_type === 'Summarization' ||
                                    entry.task_type === 'ContentGeneration')
                        );

                    const primaryOptedOut = out.hasLabels === false;
                    const primarySubmitAi = shouldSubmitAiLabelMappings(out);
                    const primaryIsClassOrNer =
                        currentUsecase?.task_type === 'ClassificationResponse' ||
                        currentUsecase?.task_type === TaskType.NER ||
                        currentUsecase?.task_type === TaskType.NER_WITH_RELATIONS;
                    const primaryGenerateAi =
                        primaryOptedOut &&
                        out.noLabelsMode !== NO_LABELS_MODE_UNLABELED &&
                        isGenerativeAiEmptyColumnTask(currentUsecase?.task_type);

                    handleDone(
                        (() => {
                            if (primarySubmitAi && primaryIsClassOrNer) {
                                return aiLabelRowsToMapping(out.aiLabels || []);
                            }
                            if (primaryOptedOut) return {};
                            return out.labelMappings || {};
                        })(),
                        exampleColumnName,
                        primaryOptedOut ? '' : out.labelColumn || '',
                        primaryOptedOut ? null : out.relationsColumn || null,
                        out.relationsMappings || {},
                        payload?.examples?.examplesAreWithPrompt,
                        payload?.examples?.promptTemplate,
                        payload?.examples?.dynamicFieldsList,
                        payload?.examples?.exampleDynamicField,
                        '', // single-objective origin column unused in multi-objective; per-subtask handled above
                        subtaskMappings.length > 1 ? subtaskMappings : null,
                        out.rejectionLabelName || null,
                        out.emptyMultilabelHandling || 'none_selected',
                        primaryGenerateAi
                    );
                }}
            />
        </div>
    );
};

export default UploadDataTab; 